import React, {useEffect, useState} from "react";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Button} from "primereact/button";
import {RadioButton} from "primereact/radiobutton";
import {Checkbox} from "primereact/checkbox";
import {InputSwitch} from "primereact/inputswitch";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {useAppDispatch, useAppSelector} from "redux/hooks";
import {classNames} from "primereact/utils";
import {
    addAudioControlBlock,
    addAudioControlCondition,
    removeAudioControlBlock,
    removeAudioControlCondition,
    setAudioControlBlocks,
    updateAudioControlBlock,
    updateAudioControlCondition,
} from "../../../../redux/actions/actionsAudioControl";
import {
    distributeEqualBlockWeights,
    distributeEqualConditionWeights,
    isAudioControlBlockActive,
    isAudioControlBlockChecked,
    isAudioControlBlockCounted,
    isAudioControlConditionActive,
    isAudioControlConditionChecked,
    isAudioControlConditionCounted,
    sumWeights,
    AUDIO_CONTROL_FAIL_SCOPE_OPTIONS,
    type AudioControlBlock,
    type AudioControlConditionFieldErrors,
} from "../audioControlQuestionnaire";
import "./AudioControlDialogDetails.scss";

const isWeightValid = (value: number) => Math.abs(value - 100) < 0.001;

const formatWeightDisplay = (value: number) => Math.round(value);

const AudioControlDialogDetails = () => {
    const dispatch = useAppDispatch();
    const audioControlData = useAppSelector(state => state.AudioControlListReducer.audioControlData);
    const errorFields = useAppSelector(state => state.AudioControlListReducer.errorFields);
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const archiveTableToggle = useAppSelector(state => state.AudioControlListReducer.archiveAudioControlTableToggle);
    const disabled = !archiveTableToggle || !editType;
    const [expandedBlocks, setExpandedBlocks] = useState<Record<number, boolean>>({});

    const blocks: AudioControlBlock[] = audioControlData?.blocks ?? [];
    const countedBlocks = blocks.filter(isAudioControlBlockCounted);
    const totalBlockWeight = sumWeights(countedBlocks);

    useEffect(() => {
        if (!errorFields?.blockErrors) {
            return;
        }

        setExpandedBlocks((previousExpandedBlocks) => {
            const updatedExpandedBlocks = {...previousExpandedBlocks};
            Object.keys(errorFields.blockErrors ?? {}).forEach((blockIndex) => {
                updatedExpandedBlocks[Number(blockIndex)] = true;
            });
            return updatedExpandedBlocks;
        });
    }, [errorFields]);

    const getBlockFieldErrors = (blockIndex: number) => errorFields?.blockErrors?.[blockIndex];

    const hasConditionFieldError = (
        blockIndex: number,
        conditionIndex: number,
        fieldName: keyof AudioControlConditionFieldErrors,
    ) => Boolean(getBlockFieldErrors(blockIndex)?.conditions?.[conditionIndex]?.[fieldName]);

    const toggleBlock = (blockIndex: number) => {
        setExpandedBlocks(previousExpandedBlocks => ({
            ...previousExpandedBlocks,
            [blockIndex]: !(previousExpandedBlocks[blockIndex] ?? false),
        }));
    };

    const onBlockRowReorder = ({dragIndex, dropIndex}: { dragIndex: number; dropIndex: number }) => {
        const reorderedBlocks = [...blocks];
        const [movedBlock] = reorderedBlocks.splice(dragIndex, 1);
        reorderedBlocks.splice(dropIndex, 0, movedBlock);

        setExpandedBlocks((previousExpandedBlocks) => {
            const updatedExpandedBlocks: Record<number, boolean> = {};
            reorderedBlocks.forEach((block, newIndex) => {
                const oldIndex = blocks.indexOf(block);
                if (previousExpandedBlocks[oldIndex]) {
                    updatedExpandedBlocks[newIndex] = true;
                }
            });
            return updatedExpandedBlocks;
        });

        dispatch(setAudioControlBlocks(reorderedBlocks));
    };

    const onConditionRowReorder = (
        blockIndex: number,
        {dragIndex, dropIndex}: { dragIndex: number; dropIndex: number }
    ) => {
        const reorderedConditions = [...(blocks[blockIndex]?.conditions ?? [])];
        const [movedCondition] = reorderedConditions.splice(dragIndex, 1);
        reorderedConditions.splice(dropIndex, 0, movedCondition);
        dispatch(updateAudioControlBlock(blockIndex, 'conditions', reorderedConditions));
    };

    const renderEvaluationCheckbox = (
        inputId: string,
        isIncludedInEvaluation: boolean,
        onChange: (nextIsIncludedInEvaluation: boolean) => void
    ) => (
        <div
            className="audio-control-evaluation-checkbox"
            onClick={() => !disabled && onChange(!isIncludedInEvaluation)}
            role="presentation"
        >
            <Checkbox
                inputId={inputId}
                checked={isIncludedInEvaluation}
                disabled={disabled}
                onChange={(changeEvent) => onChange(changeEvent.checked ?? false)}
            />
        </div>
    );

    const renderActiveSwitch = (
        inputId: string,
        isActive: boolean,
        onChange: (nextIsActive: boolean) => void,
        options: { showLabel?: boolean; labelPlacement?: 'inline' | 'top'; showHeaderLabel?: boolean; disabled?: boolean } = {}
    ) => {
        const {showLabel = true, labelPlacement = 'inline', showHeaderLabel = true} = options;
        const isSwitchDisabled = options.disabled ?? disabled;
        const stateLabel = isActive ? 'Активний' : 'Не активний';

        if (labelPlacement === 'top') {
            return (
                <div className="audio-control-active-switch audio-control-active-switch--top field mb-0">
                    {showHeaderLabel && (
                        <label htmlFor={inputId} className="audio-control-active-switch-label--top">
                            Активний
                        </label>
                    )}
                    <div className="audio-control-active-switch-control">
                        <InputSwitch
                            inputId={inputId}
                            checked={isActive}
                            disabled={isSwitchDisabled}
                            onChange={(changeEvent) => onChange(changeEvent.value)}
                        />
                    </div>
                    <span className="p-hidden-accessible">{stateLabel}</span>
                </div>
            );
        }

        return (
            <div className="audio-control-active-switch flex align-items-center gap-2">
                <InputSwitch
                    inputId={inputId}
                    checked={isActive}
                    disabled={isSwitchDisabled}
                    onChange={(changeEvent) => onChange(changeEvent.value)}
                />
                {showLabel ? (
                    <label htmlFor={inputId} className="audio-control-active-switch-label">
                        {stateLabel}
                    </label>
                ) : (
                    <label htmlFor={inputId} className="p-hidden-accessible">
                        {stateLabel}
                    </label>
                )}
            </div>
        );
    };

    const distributeBlocksEqually = () => {
        dispatch(setAudioControlBlocks(distributeEqualBlockWeights(blocks)));
    };

    const distributeConditionsEqually = (blockIndex: number) => {
        const blockConditions = blocks[blockIndex]?.conditions ?? [];
        dispatch(updateAudioControlBlock(
            blockIndex,
            'conditions',
            distributeEqualConditionWeights(blockConditions)
        ));
    };

    const renderBlock = (block: AudioControlBlock, blockIndex: number) => {
        const conditions = block.conditions ?? [];
        const countedConditions = conditions.filter(isAudioControlConditionCounted);
        const blockConditionsWeight = sumWeights(countedConditions);
        const isBlockExpanded = expandedBlocks[blockIndex] ?? false;

        const isBlockChecked = isAudioControlBlockChecked(block);
        const isBlockActive = isAudioControlBlockActive(block);
        const blockFieldErrors = getBlockFieldErrors(blockIndex);
        const hasBlockErrors = Boolean(blockFieldErrors);

        return (
            <div className="audio-control-block-wrapper">
                <div className={classNames('audio-control-block-card', {
                    'audio-control-block-card--inactive': !isBlockActive,
                    'audio-control-block-card--expanded': isBlockExpanded,
                    'audio-control-block-card--invalid': hasBlockErrors,
                })}>
                    <div className="audio-control-block-fields">
                        <div className="audio-control-block-col-leading audio-control-block-col-leading--header">
                            <div className="audio-control-block-leading audio-control-block-leading--header">
                                <button
                                    type="button"
                                    className="audio-control-block-toggle audio-control-block-toggle--header"
                                    onClick={() => toggleBlock(blockIndex)}
                                    aria-label={isBlockExpanded ? 'Згорнути блок' : 'Розгорнути блок'}
                                >
                                    <i className={classNames('pi', {
                                        'pi-chevron-right': !isBlockExpanded,
                                        'pi-chevron-down': isBlockExpanded,
                                    })}/>
                                </button>
                                <span className="audio-control-block-reorder-slot" aria-hidden="true"/>
                                <span className="audio-control-block-index">#</span>
                                <span className="audio-control-count-radio-spacer" aria-hidden="true"/>
                            </div>
                        </div>
                        <span className="audio-control-block-col-name audio-control-block-col-name--header">Назва блоку</span>
                        <span className="audio-control-block-col-weight audio-control-block-col-weight--header">Вага</span>
                        <span className="audio-control-block-col-comment audio-control-block-col-comment--header">Коментар (мета)</span>
                        <span className="audio-control-block-col-active audio-control-block-col-active--header">Активний</span>
                        <span className="audio-control-block-col-delete audio-control-block-col-delete--header" aria-hidden="true"/>
                        <div className="audio-control-block-col-leading">
                            <div className="audio-control-block-leading">
                                <span className="audio-control-block-toggle-spacer" aria-hidden="true"/>
                                <span className="audio-control-block-reorder-slot" aria-hidden="true"/>
                                <span className="audio-control-block-index">{blockIndex + 1}</span>
                                {renderEvaluationCheckbox(
                                    `block-count-${blockIndex}`,
                                    isBlockChecked,
                                    (nextIsChecked) => dispatch(updateAudioControlBlock(blockIndex, 'isChecked', nextIsChecked))
                                )}
                            </div>
                        </div>
                        <div className="audio-control-block-col-name">
                            <InputText
                                id={`block-name-${blockIndex}`}
                                className={classNames('w-full', {'p-invalid': blockFieldErrors?.name})}
                                value={block.name ?? ''}
                                disabled={disabled}
                                onChange={(changeEvent) => dispatch(updateAudioControlBlock(blockIndex, 'name', changeEvent.target.value))}
                            />
                        </div>
                        <div className="audio-control-block-col-weight">
                            <InputNumber
                                id={`block-weight-${blockIndex}`}
                                className={classNames('w-full', {'p-invalid': blockFieldErrors?.weight})}
                                suffix="%"
                                min={0}
                                max={100}
                                minFractionDigits={0}
                                maxFractionDigits={1}
                                disabled={disabled || !isBlockActive}
                                value={isBlockActive ? (block.weight ?? null) : 0}
                                onValueChange={(valueChangeEvent) => dispatch(updateAudioControlBlock(blockIndex, 'weight', valueChangeEvent.value))}
                            />
                        </div>
                        <div className="audio-control-block-col-comment">
                            <InputText
                                id={`block-comment-${blockIndex}`}
                                className="w-full"
                                maxLength={120}
                                disabled={disabled}
                                value={block.comment ?? ''}
                                onChange={(changeEvent) => dispatch(updateAudioControlBlock(blockIndex, 'comment', changeEvent.target.value))}
                            />
                        </div>
                        <div className="audio-control-block-col-active">
                            {renderActiveSwitch(
                                `block-active-${blockIndex}`,
                                isBlockActive,
                                (nextIsActive) => dispatch(updateAudioControlBlock(blockIndex, 'isActive', nextIsActive)),
                                {showLabel: false}
                            )}
                        </div>
                        <div className="audio-control-block-col-delete">
                            <Button
                                type="button"
                                icon="pi pi-trash"
                                className="p-button-outlined p-button-danger audio-control-block-delete"
                                disabled={disabled || blocks.length <= 1}
                                onClick={() => dispatch(removeAudioControlBlock(blockIndex))}
                            />
                        </div>
                    </div>

                {isBlockExpanded && (
                    <>
                        <div className="audio-control-block-conditions">
                        <DataTable
                            value={conditions}
                            reorderableRows={!disabled}
                            onRowReorder={(reorderEvent) => onConditionRowReorder(blockIndex, reorderEvent)}
                            rowClassName={(condition) => (
                                !isBlockActive || !isAudioControlConditionActive(condition)
                                    ? 'audio-control-condition-row--inactive'
                                    : ''
                            )}
                            className="w-full audio-control-conditions-table"
                            tableStyle={{tableLayout: 'fixed', width: '100%'}}
                            emptyMessage={<></>}
                        >
                            <Column
                                rowReorder
                                headerClassName="audio-control-condition-col-reorder"
                                bodyClassName="audio-control-condition-col-reorder"
                            />
                            <Column
                                headerStyle={{width: '5%'}}
                                bodyStyle={{width: '5%'}}
                                headerClassName="audio-control-condition-col-leading"
                                bodyClassName="audio-control-condition-col-leading"
                                header={() => (
                                    <div className="audio-control-condition-leading audio-control-condition-leading--header">
                                        <span className="audio-control-block-toggle-spacer" aria-hidden="true"/>
                                        <span className="audio-control-condition-reorder-slot" aria-hidden="true"/>
                                        <span className="audio-control-condition-index">#</span>
                                        <span className="audio-control-count-radio-spacer" aria-hidden="true"/>
                                    </div>
                                )}
                                body={(condition, {rowIndex: conditionIndex}) => (
                                    <div className="audio-control-condition-leading">
                                        <span className="audio-control-block-toggle-spacer" aria-hidden="true"/>
                                        <span className="audio-control-condition-reorder-slot" aria-hidden="true"/>
                                        <span className="audio-control-condition-index">{conditionIndex + 1}</span>
                                        {renderEvaluationCheckbox(
                                            `condition-count-${blockIndex}-${conditionIndex}`,
                                            isAudioControlConditionChecked(condition),
                                            (nextIsChecked) => dispatch(updateAudioControlCondition(
                                                blockIndex,
                                                conditionIndex,
                                                'isChecked',
                                                nextIsChecked
                                            ))
                                        )}
                                        <span className="audio-control-condition-trailing-spacer" aria-hidden="true"/>
                                    </div>
                                )}
                            />
                            <Column
                                header="Назва елемента"
                                headerStyle={{width: '12%'}}
                                bodyStyle={{width: '12%'}}
                                headerClassName="audio-control-condition-col-name"
                                bodyClassName="audio-control-condition-col-name"
                                body={(condition, {rowIndex: conditionIndex}) => (
                                    <InputText
                                        id={`condition-element-${blockIndex}-${conditionIndex}`}
                                        className={classNames('w-full', {
                                            'p-invalid': hasConditionFieldError(blockIndex, conditionIndex, 'condition'),
                                        })}
                                        disabled={disabled}
                                        value={condition.condition ?? ''}
                                        onChange={(changeEvent) => dispatch(updateAudioControlCondition(
                                            blockIndex,
                                            conditionIndex,
                                            'condition',
                                            changeEvent.target.value
                                        ))}
                                    />
                                )}
                            />
                            <Column
                                header="Сценарій"
                                headerStyle={{width: '24%'}}
                                bodyStyle={{width: '24%'}}
                                headerClassName="audio-control-condition-col-scenario"
                                bodyClassName="audio-control-condition-col-scenario"
                                body={(condition, {rowIndex: conditionIndex}) => (
                                    <InputText
                                        id={`condition-scenario-${blockIndex}-${conditionIndex}`}
                                        className={classNames('w-full', {
                                            'p-invalid': hasConditionFieldError(blockIndex, conditionIndex, 'scenario'),
                                        })}
                                        disabled={disabled}
                                        value={condition.scenario ?? ''}
                                        onChange={(changeEvent) => dispatch(updateAudioControlCondition(
                                            blockIndex,
                                            conditionIndex,
                                            'scenario',
                                            changeEvent.target.value
                                        ))}
                                    />
                                )}
                            />
                            <Column
                                header="Вага"
                                className="max-w-5rem w-5rem"
                                headerStyle={{width: '6%'}}
                                bodyStyle={{width: '6%'}}
                                headerClassName="audio-control-condition-col-weight max-w-5rem w-5rem"
                                bodyClassName="audio-control-condition-col-weight max-w-5rem w-5rem"
                                body={(condition, {rowIndex: conditionIndex}) => {
                                    const isConditionActive = isBlockActive && isAudioControlConditionActive(condition);

                                    return (
                                    <InputNumber
                                        id={`condition-weight-${blockIndex}-${conditionIndex}`}
                                        className={classNames('w-full', {
                                            'p-invalid': hasConditionFieldError(blockIndex, conditionIndex, 'weight'),
                                        })}
                                        suffix="%"
                                        min={0}
                                        max={100}
                                        minFractionDigits={0}
                                        maxFractionDigits={1}
                                        disabled={disabled || !isConditionActive}
                                        value={isConditionActive ? (condition.weight ?? null) : 0}
                                        onValueChange={(valueChangeEvent) => dispatch(updateAudioControlCondition(
                                            blockIndex,
                                            conditionIndex,
                                            'weight',
                                            valueChangeEvent.value
                                        ))}
                                    />
                                    );
                                }}
                            />
                            <Column
                                header="Fail"
                                headerStyle={{width: '24%'}}
                                bodyStyle={{width: '24%'}}
                                headerClassName="audio-control-condition-col-fail"
                                bodyClassName="audio-control-condition-col-fail"
                                body={(condition, {rowIndex: conditionIndex}) => (
                                    <InputText
                                        id={`condition-fail-${blockIndex}-${conditionIndex}`}
                                        className={classNames('w-full', {
                                            'p-invalid': hasConditionFieldError(blockIndex, conditionIndex, 'failCondition'),
                                        })}
                                        disabled={disabled}
                                        value={condition.failCondition ?? ''}
                                        onChange={(changeEvent) => dispatch(updateAudioControlCondition(
                                            blockIndex,
                                            conditionIndex,
                                            'failCondition',
                                            changeEvent.target.value
                                        ))}
                                    />
                                )}
                            />
                            <Column
                                headerStyle={{width: '12%'}}
                                bodyStyle={{width: '12%'}}
                                headerClassName="audio-control-condition-col-scope audio-control-condition-scope-header-cell"
                                bodyClassName="audio-control-condition-col-scope audio-control-condition-scope-cell"
                                header={() => (
                                    <div className="audio-control-condition-scope-header">
                                        <span>Елемент</span>
                                        <span>Блок</span>
                                        <span>Анкета</span>
                                    </div>
                                )}
                                body={(condition, {rowIndex: conditionIndex}) => (
                                    <div className="audio-control-condition-scope-options">
                                        {AUDIO_CONTROL_FAIL_SCOPE_OPTIONS.map((scopeOption) => (
                                            <div
                                                key={scopeOption.value}
                                                className="audio-control-condition-scope-option"
                                            >
                                                <RadioButton
                                                    inputId={`fail-scope-${blockIndex}-${conditionIndex}-${scopeOption.value}`}
                                                    name={`fail-scope-${blockIndex}-${conditionIndex}`}
                                                    value={scopeOption.value}
                                                    checked={(condition.failScope ?? 'element') === scopeOption.value}
                                                    disabled={disabled}
                                                    onChange={() => dispatch(updateAudioControlCondition(
                                                        blockIndex,
                                                        conditionIndex,
                                                        'failScope',
                                                        scopeOption.value,
                                                    ))}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            />
                            <Column
                                header="Активний"
                                headerStyle={{width: '5%'}}
                                bodyStyle={{width: '5%'}}
                                headerClassName="audio-control-condition-col-active"
                                bodyClassName="audio-control-condition-col-active audio-control-condition-active-cell"
                                body={(condition, {rowIndex: conditionIndex}) => renderActiveSwitch(
                                    `condition-active-${blockIndex}-${conditionIndex}`,
                                    isBlockActive && isAudioControlConditionActive(condition),
                                    (nextIsActive) => dispatch(updateAudioControlCondition(
                                        blockIndex,
                                        conditionIndex,
                                        'isActive',
                                        nextIsActive
                                    )),
                                    {showLabel: false, disabled: disabled || !isBlockActive}
                                )}
                            />
                            <Column
                                header=""
                                headerStyle={{width: '2.75rem', minWidth: '2.75rem', maxWidth: '2.75rem'}}
                                bodyStyle={{width: '2.75rem', minWidth: '2.75rem', maxWidth: '2.75rem'}}
                                headerClassName="audio-control-condition-col-delete"
                                bodyClassName="audio-control-condition-col-delete audio-control-condition-delete-cell"
                                body={(_condition, {rowIndex: conditionIndex}) => (
                                    <Button
                                        type="button"
                                        icon="pi pi-times"
                                        className="p-button-text p-button-danger audio-control-condition-delete"
                                        disabled={disabled || conditions.length <= 1}
                                        onClick={() => dispatch(removeAudioControlCondition(blockIndex, conditionIndex))}
                                    />
                                )}
                            />
                        </DataTable>

                        <small className="block mb-2 font-bold">
                            <span>Сума ваг елементів: </span>
                            <span className={classNames({
                                'p-error': isBlockActive && !isWeightValid(blockConditionsWeight),
                            })}>
                                {formatWeightDisplay(blockConditionsWeight)}% {isBlockActive && !isWeightValid(blockConditionsWeight) ? '(має бути 100%)' : ''}
                            </span>
                        </small>

                        <div className="flex gap-2 mt-3">
                            <Button
                                type="button"
                                icon="pi pi-plus"
                                label="Додати елемент"
                                className="p-button-primary max-w-16rem"
                                disabled={disabled}
                                onClick={() => dispatch(addAudioControlCondition(blockIndex))}
                            />
                            <Button
                                type="button"
                                icon="pi pi-percentage"
                                label="Розподілити порівну"
                                className="p-button-outlined max-w-16rem"
                                disabled={disabled || !isBlockActive || countedConditions.length === 0}
                                onClick={() => distributeConditionsEqually(blockIndex)}
                            />
                        </div>
                        </div>
                    </>
                )}
                </div>
            </div>
        );
    };

    return (
        <div className="general-settings">
            <DataTable
                value={blocks}
                reorderableRows={!disabled}
                onRowReorder={onBlockRowReorder}
                className="w-full audio-control-blocks-table"
                tableStyle={{tableLayout: 'fixed', width: '100%'}}
                emptyMessage={<></>}
            >
                <Column rowReorder headerClassName="audio-control-block-col-reorder" bodyClassName="audio-control-block-col-reorder"/>
                <Column body={(block, {rowIndex: blockIndex}) => renderBlock(block, blockIndex)}/>
            </DataTable>

            <div className="grid p-fluid mt-3">
                <div className="col-12 flex justify-content-start gap-2">
                    <Button
                        type="button"
                        icon="pi pi-plus"
                        label="Додати блок"
                        className="p-button-primary max-w-16rem"
                        disabled={disabled}
                        onClick={() => dispatch(addAudioControlBlock())}
                    />
                    <Button
                        type="button"
                        icon="pi pi-percentage"
                        label="Розподілити порівну"
                        className="p-button-outlined max-w-16rem"
                        disabled={disabled || countedBlocks.length === 0}
                        onClick={distributeBlocksEqually}
                    />
                </div>

                <div className="col-12 font-bold mt-2">
                    <span>Загальна вага блоків </span>
                    <span className={classNames({
                        'p-error': !isWeightValid(totalBlockWeight) || Boolean(errorFields?.questionnaire),
                    })}>
                        {formatWeightDisplay(totalBlockWeight)}% {!isWeightValid(totalBlockWeight) ? '(має бути 100%)' : ''}
                    </span>
                </div>
                {errorFields?.blocks?.blocks && (
                    <div className="col-12 mt-1">
                        <small className="p-error">{errorFields.blocks.blocks}</small>
                    </div>
                )}
                {errorFields?.questionnaire?.questionnaire && (
                    <div className="col-12 mt-1">
                        <small className="p-error">{errorFields.questionnaire.questionnaire}</small>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AudioControlDialogDetails;
